import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EmissionValueWithSuffix } from '../../../../../../../../../../../shared/components/emission-value-with-suffix/EmissionValueWithSuffix';
import { CountryCodeToLabelMap } from '../../../../../../../../../../../shared/helpers/country-region-helpers';
import {
  DeclaredUnit,
  DeclaredUnitToPlainSymbolsMap,
} from '../../../../../../../../../../../shared/interfaces/IComponentDetails';
import {
  EmissionSymbol,
  IProcessDetails,
} from '../../../../../../../../../../../shared/interfaces/IFootprint';
import {
  ENERGY_SOURCE,
  EnergySourceTranslationKeyMap,
  EnergySourceUnitMap,
} from '../../../PcfWizard.type';

type ExpandableIndividualProcessMachineDetailsProps = {
  machines: IProcessDetails['machines'];
  totalMachineEmissions?: number;
};

const ExpandableIndividualProcessMachineDetails = ({
  machines,
  totalMachineEmissions,
}: ExpandableIndividualProcessMachineDetailsProps) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  if (!machines || machines.length === 0) {
    return null;
  }

  // Get the declared unit from machine source
  const declaredUnit = EnergySourceUnitMap[machines?.[0]?.energy_source as ENERGY_SOURCE];

  const onExpandClick = () => {
    setIsExpanded(!isExpanded);
  };

  // Helper function to get proper source
  const getSource = () => {
    if (machines?.[0]?.energy_source === ENERGY_SOURCE.ELECTRICITY) {
      if (machines?.[0].energy_source_details?.countryCode) {
        // If a country is selected in the use of energy dropdown from the very beginning of the wizard, then show the country and year as a source
        return `${
          CountryCodeToLabelMap[machines?.[0].energy_source_details?.countryCode]
        } | ${machines?.[0].energy_source_details?.year}`;
      } else {
        // Otherwise, user has selected a manual use of energy. In this case, show the custom name as source
        return machines?.[0].energy_source_details?.name;
      }
    } else {
      return machines?.[0]?.energy_source_details?.source;
    }
  };

  // Helper function to get proper emission factor
  const getEmissionFactor = (): JSX.Element => {
    if (machines?.[0]?.energy_source === ENERGY_SOURCE.ELECTRICITY) {
      return (
        <EmissionValueWithSuffix
          emissionValue={machines?.[0].energy_source_details?.emissionFactor}
          declaredUnit={DeclaredUnit.kilowattHour}
          unitaryProductAmount={1}
          emissionSymbol={EmissionSymbol.G_CO2E}
          abbreviateUnit={true}
          emissionValueDecimals={6}
          numericalValueTextClassName='text-lg'
          suffixTextClassName='text-lg'
        />
      );
    } else {
      return (
        <EmissionValueWithSuffix
          emissionValue={machines?.[0].energy_source_details?.emissionFactor}
          declaredUnit={declaredUnit}
          unitaryProductAmount={1}
          emissionSymbol={EmissionSymbol.KG_CO2E}
          abbreviateUnit={true}
          emissionValueDecimals={6}
          numericalValueTextClassName='text-lg'
          suffixTextClassName='text-lg'
        />
      );
    }
  };

  const expandedContent = (
    <div className='flex flex-column gap-4'>
      <div className='flex flex-column w-full gap-3'>
        <div className='grid w-full grid-nogutter'>
          {/* Energy per Piece */}
          <div className='col-6 flex flex-column gap-1 pr-2'>
            <span className='text-gray-700 font-medium'>
              {t('calculationDetailsPage.sections.individualProcess.machine.energyPerPiece')}
            </span>
            <span className='text-lg'>
              {machines?.[0]?.amount} {DeclaredUnitToPlainSymbolsMap[declaredUnit]}
            </span>
          </div>

          {/* Energy Source */}
          <div className='col-6 flex flex-column gap-1 pl-2'>
            <span className='text-gray-700 font-medium'>
              {t('calculationDetailsPage.sections.individualProcess.machine.energySource')}
            </span>
            <span className='text-lg'>
              {t(EnergySourceTranslationKeyMap[machines?.[0]?.energy_source as ENERGY_SOURCE])}
            </span>
          </div>
        </div>

        <div className='grid w-full grid-nogutter'>
          {/* Source */}
          <div className='col-6 flex flex-column gap-1 pr-2'>
            <span className='text-gray-700 font-medium'>
              {t('calculationDetailsPage.sections.individualProcess.machine.source')}
            </span>
            <span className='text-lg'>{getSource()}</span>
          </div>

          {/* Emission Factor */}
          <div className='col-6 flex flex-column gap-1 pl-2'>
            <span className='text-gray-700 font-medium'>
              {t('calculationDetailsPage.sections.individualProcess.machine.emissionFactor')}
            </span>
            <span className='text-lg'>{getEmissionFactor()}</span>
          </div>
        </div>

        {/* Comment */}
        {/* Show only when energy source is not electricity. This is because Electricity has its own comment that is shown earlier in above details */}
        {machines?.[0]?.energy_source !== ENERGY_SOURCE.ELECTRICITY &&
          machines?.[0]?.energy_source_details?.comment && (
            <div className='flex flex-column gap-1'>
              <span className='text-gray-700 font-medium'>
                {t('calculationDetailsPage.sections.individualProcess.machine.comment')}
              </span>
              <span>{machines?.[0]?.energy_source_details?.comment}</span>
            </div>
          )}

        {/* Comment / Calculation Method */}
        {machines?.[0]?.comment && (
          <div className='flex flex-column gap-1'>
            <span className='text-gray-700 font-medium'>
              {t(
                'calculationDetailsPage.sections.individualProcess.machine.commentCalculationMethod',
              )}
            </span>
            <span>{machines?.[0]?.comment}</span>
          </div>
        )}
      </div>

      <Divider className='p-2 m-0 opacity-60' />
    </div>
  );

  return (
    <div className='flex flex-column gap-3 w-full'>
      {/* Title & Expand Button */}
      <div className='flex flex-row gap-2 align-items-center'>
        <span className='text-lg font-medium'>{machines?.[0]?.name}</span>

        <Button
          icon={isExpanded ? 'pi pi-angle-up' : 'pi pi-angle-down'}
          onClick={onExpandClick}
          text
          className='p-2 m-0 -my-1 text-black-alpha-90 w-min hover:bg-black-alpha-20 border-round-lg bg-black-alpha-10 opacity-60'
          pt={{ icon: { className: 'font-bold' } }}
        />
      </div>

      {/* Expanded Content */}
      {isExpanded && expandedContent}

      {/* Emissions */}
      <div className='flex flex-column gap-1'>
        <span className='text-gray-700 font-medium'>
          {t('calculationDetailsPage.sections.injectionMoldingProcess.mainMachine.emissions')}
        </span>
        <div className='text-lg'>
          <EmissionValueWithSuffix
            emissionValue={totalMachineEmissions}
            declaredUnit={DeclaredUnit.piece}
            unitaryProductAmount={1}
            emissionSymbol={EmissionSymbol.KG_CO2E}
            abbreviateUnit={true}
            emissionValueDecimals={6}
            numericalValueTextClassName='text-lg'
            suffixTextClassName='text-lg'
          />
        </div>
      </div>
    </div>
  );
};

export default ExpandableIndividualProcessMachineDetails;
